// Helpers that dig through the Bopayment API response envelope for the Orenda
// endpoints. Backend returns:
//   { payload: { data: { data: { application, status } }, errorMessage: "<stringified raw>", isSuccess, statusCode }, ... }
// We normalise to a flat { application, status, raw } shape for the UI.

export const parseApplicationResponse = (response) => {
  const inner = response?.payload?.data?.data ?? response?.data?.data ?? null;
  let raw = null;
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      raw = JSON.parse(rawStr);
    } catch {
      raw = null;
    }
  }
  return {
    application: inner?.application ?? raw?.data?.application ?? null,
    status: inner?.status ?? raw?.data?.status ?? null,
    raw: raw?.data ?? inner ?? null,
  };
};

// Unwraps the KYC schema response and normalises the step-1/step-2/step-3
// convention into an ordered array so the UI can iterate cleanly. Each entry
// is { key, name, description, image, fields } — key is "step-1" etc, fields
// is the KycField[] for that step.
export const parseKycSchemaResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let root = null;
  if (typeof rawStr === "string") {
    try {
      root = JSON.parse(rawStr);
    } catch {
      root = null;
    }
  }
  const schema =
    root?.data?.schema ??
    response?.payload?.data?.data?.schema ??
    response?.data?.data?.schema ??
    null;

  if (!schema) return { schemaId: null, steps: [], schema: null };

  const stepKeys = Object.keys(schema)
    .filter((k) => /^step-\d+$/.test(k))
    .sort((a, b) => {
      const na = Number(a.replace("step-", ""));
      const nb = Number(b.replace("step-", ""));
      return na - nb;
    });

  const info = schema.schemaInfo || {};
  const steps = stepKeys.map((k) => ({
    key: k,
    name: info[k]?.stepName || k,
    description: info[k]?.stepDescription || "",
    image: info[k]?.stepImage || null,
    fields: Array.isArray(schema[k]) ? schema[k] : [],
  }));

  return { schemaId: schema.schemaId, steps, schema };
};

// Unwraps the create-card response: { id, customerId, accountId, cardType,
// status, provider }. Backend wraps it in the same payload envelope as every
// other Orenda call, plus a stringified `errorMessage` carrying the raw.
export const parseCardCreateResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      if (parsed?.data?.id) return parsed.data;
    } catch {
      // fall through
    }
  }
  const inner = response?.payload?.data?.data ?? response?.data?.data ?? null;
  if (inner?.id) return inner;
  return null;
};

// Unwraps { success, data: { token, expiresAt } } from either the parsed
// envelope or the stringified errorMessage. Returns { token, expiresAt }.
export const parseSumsubTokenResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      if (parsed?.data?.token) {
        return {
          token: parsed.data.token,
          expiresAt: parsed.data.expiresAt ?? null,
        };
      }
    } catch {
      // fall through
    }
  }
  const inner = response?.payload?.data?.data ?? response?.data?.data ?? null;
  return {
    token: inner?.token ?? null,
    expiresAt: inner?.expiresAt ?? null,
  };
};

// Unwraps the passkey-session response. Depending on how the backend chains
// the Orenda calls, the shape may be either the raw secure-key response
//   { success, data: { secretKey, cipher } }
// or the chained passkey-challenge response
//   { success, data: { passkeySession, fido2options: { challenge, rpId, allowCredentials, ... } } }
// We extract both shapes and let the caller use whichever fields are present.
export const parsePassKeySessionResponse = (response) => {
  // The backend stitches cipher from a separate /secure-key call into the
  // typed response AFTER Orenda's raw challenge body is captured in
  // errorMessage. So errorMessage omits cipher — only the typed envelope
  // path has it. Prefer the envelope, then try the stringified fallback
  // for everything else.
  const envelopeData =
    response?.payload?.data?.data?.data ??
    response?.payload?.data?.data ??
    response?.data?.data ??
    null;

  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let rawData = null;
  if (typeof rawStr === "string") {
    try {
      const root = JSON.parse(rawStr);
      rawData = root?.data ?? null;
    } catch { rawData = null; }
  }

  if (!envelopeData && !rawData) {
    return { cipher: null, secretKey: null, passkeySession: null, fido2options: null };
  }
  // For each field: envelope first (has cipher), then rawData as backup.
  const pick = (key) => envelopeData?.[key] ?? rawData?.[key] ?? null;
  return {
    cipher: pick("cipher"),
    secretKey: pick("secretKey"),
    passkeySession: pick("passkeySession"),
    fido2options: pick("fido2options"),
  };
};

// Unwraps the secure-card-details response:
//   { success, data: { cardId, pan, cvc2, expiry, printedName } }
export const parseSecureCardDetailsResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let root = null;
  if (typeof rawStr === "string") {
    try { root = JSON.parse(rawStr); } catch { root = null; }
  }
  const data =
    root?.data ??
    response?.payload?.data?.data ??
    response?.data?.data ??
    null;
  if (!data) return null;
  return {
    cardId: data.cardId ?? null,
    pan: data.pan ?? null,
    cvc2: data.cvc2 ?? null,
    expiry: data.expiry ?? null,
    printedName: data.printedName ?? null,
  };
};

// Unwraps the start-add-passkey response. The response envelope's `data.data`
// often loses the nested publicKey when the backend uses JObject + RestSharp
// default deserializer — the full payload survives verbatim in the stringified
// `errorMessage` field, so prefer that source.
//
// Returns a normalised { needsEmail, sessionToken, publicKey, challenge }.
export const parseStartAddPasskeyResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let data = null;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      data = parsed?.data ?? parsed;
    } catch {
      /* fall through to envelope path */
    }
  }
  if (!data) {
    data =
      response?.payload?.data?.data ??
      response?.data?.data ??
      response?.data ??
      null;
  }
  if (!data) {
    return { needsEmail: false, sessionToken: null, publicKey: null, challenge: null };
  }

  const challenge = data.challenge ?? null;
  // v2 renames session_token → sessionToken; keep the fallback for the
  // very first deploy where backend/FE may be a step apart.
  const sessionToken = data.sessionToken ?? data.session_token ?? null;
  const publicKey = data.publicKey && Object.keys(data.publicKey).length > 0
    ? data.publicKey
    : null;

  const needsEmail =
    (typeof challenge === "string" && challenge.toUpperCase().includes("EMAIL")) ||
    (!publicKey && !sessionToken);

  return { needsEmail, sessionToken, publicKey, challenge };
};

// Unwraps finish-add-passkey. Same envelope trick in case the backend
// response strips nested data.
export const parseFinishAddPasskeyResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      return { success: !!parsed?.success, data: parsed?.data ?? null };
    } catch {
      /* fall through */
    }
  }
  const data =
    response?.payload?.data?.data ??
    response?.data?.data ??
    response?.data ??
    null;
  return { success: !!(data && (data.success ?? true)), data };
};

export const parseLegalDocumentsResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      return {
        documents: parsed?.data?.documents ?? null,
        currentAcceptance: parsed?.data?.currentAcceptance ?? null,
        accountType: parsed?.data?.accountType ?? null,
        raw: parsed?.data ?? null,
      };
    } catch {
      // fall through
    }
  }
  const inner = response?.payload?.data?.data?.data ?? response?.data?.data ?? null;
  return {
    documents: inner?.documents ?? null,
    currentAcceptance: inner?.currentAcceptance ?? null,
    accountType: inner?.accountType ?? null,
    raw: inner,
  };
};
